import React, { useEffect, useState } from 'react';
import { containerMaxWXl, ContentContainer } from '../../components/ContentContainer/contentContainer';
import { useParams } from 'react-router-dom';
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { ethers } from 'ethers';
import { useUserProvider } from '../../providers/User/userProvider';
import { useERC20Balances, useMoralisCloudFunction } from 'react-moralis';
import useGames from '../../providers/Games/useGames';
import { Games } from './Games/games';
import { Header } from './Header/header';
import { blockchainConfig } from 'src/blockchain-config';
import Moralis from 'moralis-v1/types';
import { t } from 'i18next';
import { TransactionsList } from '../../components/TransactionsList/transactionsList';
import useDidMountEffect from '../../utils/hooks/effects/didMount';
import { Color } from '../../theme/color';

enum State {
	LOADING,
	FOUND,
	INVALID,
}

export interface IGameToken {
	address?: string;
	balance?: string;
}

export const User = () => {
	const [state, handleState] = useState(State.LOADING);

	const games = useGames();
	const [userGameTokens, setUserGameTokens] = useState<IGameToken[]>([]);
	const [isUserGameDataLoading, setIsUserGameDataLoading] = useState(false);

	const { isLoading, data, fetch } = useUserProvider();

	const { id: id_ } = useParams();
	const id = id_ ?? ethers.constants.AddressZero;

	const erc20 = useERC20Balances();
	const listedGamesAddressesQuery = useMoralisCloudFunction('getListedGamesAddresses');

	/** User-specific game-data */
	useDidMountEffect(() => {
		if (!data.address || data.address === ethers.constants.AddressZero) {
			return;
		}

		void erc20.fetchERC20Balances({
			params: {
				chain: blockchainConfig.NETWORK_DEFAULT.NAME as Moralis.Chain as any,
				address: data.address,
			},
		});
	}, [data.address]);

	useEffect(() => setIsUserGameDataLoading(!erc20.isLoading && erc20.isFetching), [erc20.isFetching]);

	useEffect(() => {
		if (!erc20.data || !listedGamesAddressesQuery.data) {
			return;
		}

		const userTokens = erc20.data.map(token => {
			return {
				address: token.token_address,
				balance: ethers.utils.formatUnits(token.balance, token.decimals),
			};
		});

		const listedGameAddresses = listedGamesAddressesQuery.data as string[];
		const userGames = userTokens.filter(token => listedGameAddresses.includes(token.address));
		const userGameAddresses = userGames.map(game => game.address);

		games.fetch(userGameAddresses);
		setUserGameTokens(userGames);
	}, [erc20.data, listedGamesAddressesQuery.data]);

	useEffect(() => {
		if (ethers.utils.isAddress(id)) {
			games.fetch(null);
			fetch(id);
		}
	}, [id]);

	useEffect(() => {
		if (!ethers.utils.isAddress(id)) {
			handleState(State.INVALID);
		} else if (isLoading) {
			handleState(State.LOADING);
		} else {
			handleState(State.FOUND);
		}
	}, [isLoading]);

	const typeOptions: { [p: number]: () => JSX.Element } = {
		[State.LOADING]: () => (
			<Flex
				height="100vh"
				justifyContent="center"
				alignItems="center"
			>
				<Spinner />
			</Flex>
		),
		[State.FOUND]: () => (
			<Flex
				direction="column"
				bg={Color.BACKGROUND_LIGHT}
				w="100%"
				pb="64px"
			>
				<Header dataHandle={id} />
				<Flex
					flexDirection="column"
					gridGap="48px"
					w="100%"
					maxW={containerMaxWXl}
					margin="auto"
					mt="64px"
					px={{ base: '0', hashup: '32px', xl: 0 }}
				>
					<Games
						data={games.data}
						state={games.state}
						userGameTokens={userGameTokens}
						isUserGameDataLoading={isUserGameDataLoading}
					/>
					<Box paddingX={{ base: '16px', hashup: '0' }}>
						<TransactionsList
							headerRightText={t('home.seeMore').toString()}
							header={t('home.latestTransaction').toString()}
							headerStyle={{
								fontSize: '14px',
								lineHeight: '18px',
								fontWeight: '400',
								textTransform: 'uppercase',
							}}
							onClickRightHeader={() => null}
							filter={{ user: { address: data.address } }}
						/>
					</Box>
				</Flex>
			</Flex>
		),
		[State.INVALID]: () => <Text>Invalid user</Text>,
	};

	return <ContentContainer isCustomThemed>{typeOptions[state]()}</ContentContainer>;
};
